/**
 * ProfileEntrepriseBankController — onglet « Banque dédiée »
 * de /profil-entreprise.html (EPIC-007 / 7.3 + 7.7).
 *
 * Pilote 1 nm-edit-block (radio yes/no/not_yet/unknown) + le résumé
 * synthétique. La bannière d'alerte du seuil PACTE est gérée
 * indépendamment côté dashboard payant.
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : le résumé est rendu depuis <template>
 *     (`tpl-bank-summary-*`) cloné via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const STATUS_LABELS = {
  yes: { txt: "Oui — compte dédié actif. Aucune alerte affichée.", cls: "nm-pill--olive" },
  no: { txt: "Non — compte personnel utilisé. Recommandation pédagogique active.", cls: "nm-pill--ochre" },
  not_yet: { txt: "Pas encore — j'envisage d'en ouvrir un. Recommandation active.", cls: "nm-pill--ochre" },
  unknown: { txt: "Je ne sais pas / je préfère ne pas répondre. Question ré-affichée plus tard.", cls: "" },
};

export default class ProfileEntrepriseBankController extends BaseController {
  static targets = [
    "blockBanque",
    "summary",
    "submit",
  ];

  state = {
    dedicatedBankAccountStatus: "unknown",
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    try {
      const data = await api.getOrganizationProfile();
      this._hydrate(data && data.bank ? data.bank : null);
    } catch (error) {
      // Hydratation silencieuse : l'utilisateur peut toujours saisir
      // un statut. On log côté console pour debug.
      console.warn("[profile-entreprise-bank] hydration failed", error);
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreRadios(this.state.dedicatedBankAccountStatus);
    this._open(block);
  }

  cancelBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreRadios(this.state.dedicatedBankAccountStatus);
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submitStatus(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const block = this.hasBlockBanqueTarget ? this.blockBanqueTarget : null;
    const newStatus = this._readSelectedRadio(block);
    if (!newStatus) {
      this.showError("Sélectionnez un statut avant d'enregistrer.");
      return;
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    try {
      const data = await api.updateBankStatus(newStatus);
      this._hydrate(data);
      if (block) this._close(block);
      this.showSuccess("Statut du compte bancaire dédié enregistré.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour du statut.";
      this.showError(msg);
    } finally {
      if (this.hasSubmitTarget) this.submitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  _hydrate(data) {
    if (!data || typeof data !== "object") return;
    const status = data.dedicatedBankAccountStatus;
    if (status && Object.hasOwn(STATUS_LABELS, status)) {
      this.state.dedicatedBankAccountStatus = status;
    }
    this._renderSummary();
    this._restoreRadios(this.state.dedicatedBankAccountStatus);
  }

  _renderSummary() {
    if (!this.hasSummaryTarget) return;
    const status = this.state.dedicatedBankAccountStatus;
    const label = STATUS_LABELS[status] ?? STATUS_LABELS.unknown;
    const fragment = this.cloneTemplate("tpl-bank-summary");
    this.fillSlots(fragment, {
      pillCls: label.cls,
      pillLabel: status,
      description: label.txt,
    });
    // Mise à jour de la classe du pill (slot ne fait que injecter du texte).
    const pill = fragment.querySelector("[data-slot-classes='pill']");
    if (pill) {
      pill.className = "nm-pill" + (label.cls ? " " + label.cls : "");
    }
    this._replaceChildren(this.summaryTarget, fragment);
  }

  _readSelectedRadio(block) {
    if (!block) return null;
    const checked = block.querySelector("input[type='radio'][name='bank']:checked");
    return checked ? checked.value : null;
  }

  _restoreRadios(status) {
    const block = this.hasBlockBanqueTarget ? this.blockBanqueTarget : null;
    if (!block) return;
    const radios = block.querySelectorAll("input[type='radio'][name='bank']");
    for (const r of radios) {
      r.checked = (r.value === status);
    }
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
